import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Settings, Copy, Palette, FileText, Globe, Code, Lightbulb, TrendingUp } from 'lucide-react';

const AppOptions = ({ appId, appName, onOptionsChange, initialOptions = {} }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [options, setOptions] = useState(initialOptions);

  const updateOption = (key, value) => {
    const newOptions = { ...options, [key]: value };
    setOptions(newOptions);
    onOptionsChange(newOptions);
  };

  // Define options for each app type
  const getAppOptions = () => {
    switch (appId) {
      case 'draft-composer':
        return (
          <>
            <div className="space-y-3">
              {/* Email Thread */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Thread / Context
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows="3"
                  placeholder="Paste previous email thread or provide context..."
                  value={options.emailThread || ''}
                  onChange={(e) => updateOption('emailThread', e.target.value)}
                />
              </div>

              {/* Tone and Purpose Selection - Same Row */}
              <div className="grid grid-cols-2 gap-3">
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  value={options.tone || 'professional'}
                  onChange={(e) => updateOption('tone', e.target.value)}
                >
                  <option value="professional">Professional Tone</option>
                  <option value="friendly">Friendly Tone</option>
                  <option value="formal">Formal Tone</option>
                  <option value="casual">Casual Tone</option>
                  <option value="enthusiastic">Enthusiastic Tone</option>
                  <option value="empathetic">Empathetic Tone</option>
                  <option value="persuasive">Persuasive Tone</option>
                </select>

                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  value={options.purpose || 'general'}
                  onChange={(e) => updateOption('purpose', e.target.value)}
                >
                  <option value="general">General Communication</option>
                  <option value="request">Making a Request</option>
                  <option value="followup">Follow-up</option>
                  <option value="introduction">Introduction</option>
                  <option value="thank-you">Thank You</option>
                  <option value="apology">Apology</option>
                  <option value="announcement">Announcement</option>
                  <option value="invitation">Invitation</option>
                </select>
              </div>

              {/* Multiple Versions */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="multipleVersions"
                  className="mr-2"
                  checked={options.multipleVersions || false}
                  onChange={(e) => updateOption('multipleVersions', e.target.checked)}
                />
                <label htmlFor="multipleVersions" className="text-sm text-gray-700">
                  Generate multiple versions
                </label>
              </div>
            </div>
          </>
        );

      case 'prd-generator':
        return (
          <>
            <div className="space-y-3">
              {/* Product Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product Type
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={options.productType || 'software'}
                  onChange={(e) => updateOption('productType', e.target.value)}
                >
                  <option value="software">Software/App</option>
                  <option value="hardware">Hardware</option>
                  <option value="service">Service</option>
                  <option value="platform">Platform</option>
                  <option value="feature">New Feature</option>
                </select>
              </div>

              {/* Target Audience */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Audience
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., B2B SaaS customers, mobile gamers..."
                  value={options.targetAudience || ''}
                  onChange={(e) => updateOption('targetAudience', e.target.value)}
                />
              </div>

              {/* Key Features */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Key Features (comma-separated)
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., AI-powered, real-time sync, mobile app..."
                  value={options.keyFeatures || ''}
                  onChange={(e) => updateOption('keyFeatures', e.target.value)}
                />
              </div>

              {/* Include Sections */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Include Sections
                </label>
                <div className="space-y-1">
                  {['User Stories', 'Technical Requirements', 'Success Metrics', 'Timeline', 'Risks'].map((section) => (
                    <label key={section} className="flex items-center">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={options[`include${section.replace(' ', '')}`] !== false}
                        onChange={(e) => updateOption(`include${section.replace(' ', '')}`, e.target.checked)}
                      />
                      <span className="text-sm text-gray-700">{section}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </>
        );

      case 'competitor-analyzer':
        return (
          <>
            <div className="space-y-3">
              {/* Your Product/Company */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Your Product/Company
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter your product or company name..."
                  value={options.yourProduct || ''}
                  onChange={(e) => updateOption('yourProduct', e.target.value)}
                />
              </div>

              {/* Competitors */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Competitors (comma-separated)
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Competitor A, Competitor B..."
                  value={options.competitors || ''}
                  onChange={(e) => updateOption('competitors', e.target.value)}
                />
              </div>

              {/* Analysis Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Analysis Focus
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={options.analysisType || 'comprehensive'}
                  onChange={(e) => updateOption('analysisType', e.target.value)}
                >
                  <option value="comprehensive">Comprehensive Analysis</option>
                  <option value="features">Feature Comparison</option>
                  <option value="pricing">Pricing Strategy</option>
                  <option value="marketing">Marketing & Positioning</option>
                  <option value="strengths-weaknesses">SWOT Analysis</option>
                  <option value="market-share">Market Share & Trends</option>
                </select>
              </div>

              {/* Include Visuals */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="includeVisuals"
                  className="mr-2"
                  checked={options.includeVisuals || false}
                  onChange={(e) => updateOption('includeVisuals', e.target.checked)}
                />
                <label htmlFor="includeVisuals" className="text-sm text-gray-700">
                  Include comparison tables/charts
                </label>
              </div>
            </div>
          </>
        );

      case 'website-auditor':
        return (
          <>
            <div className="space-y-3">
              {/* Website URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Website URL
                </label>
                <input
                  type="url"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://example.com"
                  value={options.websiteUrl || ''}
                  onChange={(e) => updateOption('websiteUrl', e.target.value)}
                />
              </div>

              {/* Audit Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Audit Focus
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={options.auditType || 'full'}
                  onChange={(e) => updateOption('auditType', e.target.value)}
                >
                  <option value="full">Full Audit</option>
                  <option value="seo">SEO Only</option>
                  <option value="ux">UX/UI Only</option>
                  <option value="performance">Performance</option>
                  <option value="accessibility">Accessibility</option>
                  <option value="mobile">Mobile Optimization</option>
                </select>
              </div>

              {/* Competitors for Comparison */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Compare with competitors (optional)
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="competitor1.com, competitor2.com..."
                  value={options.competitorUrls || ''}
                  onChange={(e) => updateOption('competitorUrls', e.target.value)}
                />
              </div>

              {/* Priority Issues Only */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="priorityOnly"
                  className="mr-2"
                  checked={options.priorityOnly || false}
                  onChange={(e) => updateOption('priorityOnly', e.target.checked)}
                />
                <label htmlFor="priorityOnly" className="text-sm text-gray-700">
                  Show priority issues only
                </label>
              </div>
            </div>
          </>
        );

      case 'code-reviewer':
        return (
          <>
            <div className="space-y-3">
              {/* Programming Language */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Programming Language
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={options.language || 'auto'}
                  onChange={(e) => updateOption('language', e.target.value)}
                >
                  <option value="auto">Auto-detect</option>
                  <option value="javascript">JavaScript/TypeScript</option>
                  <option value="python">Python</option>
                  <option value="java">Java</option>
                  <option value="csharp">C#</option>
                  <option value="go">Go</option>
                  <option value="rust">Rust</option>
                  <option value="cpp">C++</option>
                  <option value="ruby">Ruby</option>
                  <option value="php">PHP</option>
                </select>
              </div>

              {/* Review Focus */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Review Focus
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={options.reviewFocus || 'general'}
                  onChange={(e) => updateOption('reviewFocus', e.target.value)}
                >
                  <option value="general">General Review</option>
                  <option value="security">Security Issues</option>
                  <option value="performance">Performance</option>
                  <option value="bestpractices">Best Practices</option>
                  <option value="readability">Code Readability</option>
                  <option value="testing">Test Coverage</option>
                </select>
              </div>

              {/* Severity Level */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Review Strictness
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={options.strictness || 'balanced'}
                  onChange={(e) => updateOption('strictness', e.target.value)}
                >
                  <option value="lenient">Lenient (Major issues only)</option>
                  <option value="balanced">Balanced</option>
                  <option value="strict">Strict (All issues)</option>
                </select>
              </div>

              {/* Include Suggestions */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="includeSuggestions"
                  className="mr-2"
                  checked={options.includeSuggestions !== false}
                  onChange={(e) => updateOption('includeSuggestions', e.target.checked)}
                />
                <label htmlFor="includeSuggestions" className="text-sm text-gray-700">
                  Include code improvement suggestions
                </label>
              </div>
            </div>
          </>
        );

      case 'brainstorm-partner':
        return (
          <>
            <div className="space-y-3">
              {/* Topic/Challenge */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Topic or Challenge
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows="2"
                  placeholder="What would you like to brainstorm about?"
                  value={options.topic || ''}
                  onChange={(e) => updateOption('topic', e.target.value)}
                />
              </div>

              {/* Brainstorming Method */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Brainstorming Method
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={options.method || 'freeform'}
                  onChange={(e) => updateOption('method', e.target.value)}
                >
                  <option value="freeform">Free-form Ideas</option>
                  <option value="mindmap">Mind Mapping</option>
                  <option value="scamper">SCAMPER Method</option>
                  <option value="sixhats">Six Thinking Hats</option>
                  <option value="reversal">Reversal Method</option>
                  <option value="whatif">What-If Scenarios</option>
                </select>
              </div>

              {/* Constraints */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Constraints (optional)
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., budget limits, time constraints, resources..."
                  value={options.constraints || ''}
                  onChange={(e) => updateOption('constraints', e.target.value)}
                />
              </div>

              {/* Quantity */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Number of Ideas
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={options.quantity || '10'}
                  onChange={(e) => updateOption('quantity', e.target.value)}
                >
                  <option value="5">5 ideas</option>
                  <option value="10">10 ideas</option>
                  <option value="20">20 ideas</option>
                  <option value="unlimited">As many as possible</option>
                </select>
              </div>
            </div>
          </>
        );

      default:
        return (
          <div className="text-sm text-gray-500">
            No specific options available for this assistant.
          </div>
        );
    }
  };

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Settings className="w-5 h-5 text-gray-600" />
          <h3 className="font-medium text-gray-900">{appName} Options</h3>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="p-1.5 rounded hover:bg-gray-200 transition-colors"
        >
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-gray-600" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-600" />
          )}
        </button>
      </div>
      
      {isExpanded && (
        <div className="mt-3">
          {getAppOptions()}
        </div>
      )}
    </div>
  );
};

export default AppOptions;